import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { API } from 'aws-amplify';
import { from, Observable } from 'rxjs';
import { Address, Fee, Phone } from './core.module';
import { FormGroup } from '@angular/forms';
import { FormService } from './form.service';
import { DEFAULT_GET_LIMIT } from '../shared/constants/operationals';
import { LongRange } from './long-range.service';

export const STORES_API_NAME: string = 'stores';
export const STORES_API_ENDPOINT: string = environment.API_BASE_PATH + '/store';

@Injectable({
  providedIn: 'root',
})
export class StoresService {
  constructor(private form_service: FormService) { }

  buildOrderRequirementsFromForm(form_group: FormGroup): OrderCreationRequirements {
    const is_enabled: boolean = form_group.get(this.form_service.is_enabled_label)?.value;
    const is_partially_enabled: boolean = form_group.get(this.form_service.is_partially_enabled_label)?.value;
    const is_web_sale_enabled: boolean = form_group.get(this.form_service.is_web_sale_enabled_label)?.value;
    const is_in_store_sale_enabled: boolean = form_group.get(this.form_service.is_in_store_sale_enabled_label)?.value;
    return {
      is_enabled,
      is_partially_enabled,
      is_web_sale_enabled,
      is_in_store_sale_enabled,
    };
  }

  buildOrderTicketRequirementsFromForm(form_group: FormGroup): OrderCreationRequirements {
    const min_amount: number = form_group.get(this.form_service.min_amount_label)?.value * 100;
    const max_amount: number = form_group.get(this.form_service.max_amount_label)?.value * 100;
    const is_bnpl_long_enabled: boolean = form_group.get(this.form_service.is_bnpl_long_enabled_label)?.value;
    return {
      min_amount,
      max_amount,
      is_bnpl_long_enabled: is_bnpl_long_enabled || false,
    };
  }

  readStore(store_id: string): Observable<any> {
    const api_name: string = STORES_API_NAME;
    const path: string = `/${store_id}`;
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }

  listStores(list_params?: ListStoresParams): Observable<any> {
    const api_name: string = STORES_API_NAME;
    const path: string = '';

    let query_string_params: any = {
      cursor: list_params?.cursor || null,
      limit: list_params?.limit || DEFAULT_GET_LIMIT,
    };
    if (list_params?.status) query_string_params['status'] = list_params.status;
    if (list_params?.name) query_string_params['name'] = list_params.name;
    if (list_params?.store_id) query_string_params['store_id'] = list_params.store_id;
    if (list_params?.cms) query_string_params['cms'] = list_params.cms;
    if (list_params?.vat) query_string_params['vat'] = list_params.vat;
    if (list_params?.business_name) query_string_params['business_name'] = list_params.business_name;
    if (list_params?.sales_network_id) query_string_params['sales_network_id'] = list_params.sales_network_id;
    if (list_params?.company_id) query_string_params['company_id'] = list_params.company_id;

    const init = { response: true, queryStringParameters: query_string_params };
    return from(API.get(api_name, path, init));
  }

  listStoresByCompanyID(company_id: string): Observable<any> {
    const api_name: string = STORES_API_NAME;
    const path: string = `/company/${company_id}`;
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }

  readSummary(): Observable<any> {
    const api_name: string = STORES_API_NAME;
    const path: string = '/summary';
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }

  setOrderRequirements(store_id: string, order_requirements: OrderCreationRequirements): Observable<any> {
    const api_name: string = STORES_API_NAME;
    const path: string = `/${store_id}/order-requirements`;
    const init = { response: true, body: { order_requirements } };
    return from(API.put(api_name, path, init));
  }

  setBnplLong(store_id: string, is_bnpl_long_enabled: boolean): Observable<any> {
    const api_name: string = STORES_API_NAME;
    const path: string = `/${store_id}/bnpl-long`;
    const init = { response: true, body: { is_bnpl_long_enabled } };
    return from(API.put(api_name, path, init));
  }

  setPluginCredentials(store_id: string): Observable<any> {
    const api_name: string = STORES_API_NAME;
    const path: string = `/${store_id}/set-plugin-credentials`;
    const init = { response: true };
    return from(API.put(api_name, path, init));
  }

  bulkCreate(text_file: string) {
    const api_name: string = STORES_API_NAME;
    const path: string = `/bulk-create`;
    const init = {
      response: true,
      body: { text_file },
    };
    return from(API.post(api_name, path, init));
  }

  setSalesNetworkId(store_id: string, sales_network_id: string): Observable<any> {
    const api_name: string = STORES_API_NAME;
    const path: string = `/${store_id}/sales-network-id`;
    const init = { response: true, body: { sales_network_id } };
    return from(API.put(api_name, path, init));
  }
}

export interface ListStoresParams {
  store_id?: string;
  name?: string;
  status?: 'enabled' | 'disabled';
  cms?: CmsValue;
  vat?: string;
  business_name?: string;
  sales_network_id?: string;
  company_id?: string;

  cursor?: string | null;
  limit?: number;
}

export interface Store {
  store_id?: string;
  company_id?: string;
  name?: string;
  business_name?: string;
  email?: string;
  vat?: string;
  sdi?: string;
  address?: Address;
  phone?: Phone;
  website?: string;
  cms?: string;
  fee?: Fee;
  logo_url?: string;
  long_ranges?: LongRange[];
  secret_keys?: SecretKey[];
  client_id?: string;
  client_secret?: string;
  order_requirements?: OrderCreationRequirements;
  plugin_preferences?: PluginPreference;
  company_custom_buyer_subdomain?: string;
  is_sales_network_id_editing_disabled?: boolean;
  agreement?: string;
  created_at?: number;
  updated_at?: number;
  sales_network_id?: string;
  company_vat?: string;
  company_business_name?: string;
  owner_id?: string;
  owner_firstname?: string;
  owner_lastname?: string;
  owner_email?: string;
}

export class SecretKey {
  value?: string;
  type?: 'live' | 'test';
  created_at?: number;
  is_disabled?: boolean;
}

export class OrderCreationRequirements {
  is_enabled?: boolean;
  is_partially_enabled?: boolean;
  is_web_sale_enabled?: boolean;
  is_in_store_sale_enabled?: boolean;
  is_on_session_enabled?: boolean;
  is_off_session_enabled?: boolean;
  is_bnpl_long_enabled?: boolean;
  is_store_review_disabled?: boolean;
  is_buyer_behalf_enabled?: boolean;
  is_seller_behalf_enabled?: boolean;
  min_amount?: number;
  max_amount?: number;
  is_sdd_payment_enabled?: boolean;
}

export class PluginPreference {
  is_product_preview_enabled?: boolean;
  is_product_preview_modal_enabled?: boolean;
  is_product_preview_price_enabled?: boolean;
  product_preview_position?: string;
  is_cart_preview_enabled?: boolean;
  is_cart_preview_price_enabled?: boolean;
  cart_preview_position?: string;
}

export interface StoreSummary {
  length: number;
  enabled: number;
  disabled: number;
}

export enum CmsValue {
  woocommerce = 'woocommerce',
  prestashop = 'prestashop',
  magento = 'magento',
  shopify = 'shopify',
  other = 'other',
}
